'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  MarkerClusterer,
  useJsApiLoader,
} from '@react-google-maps/api';
import type { Cluster } from '@react-google-maps/marker-clusterer';

import { MapCarousel } from './Carousel';
import './styles.css';
import { Locale } from '../../../i18n.config';
import { getTranslation } from '@/lib/utils';
import { useMap } from '@/hooks/useMapContext';
import { MapMarkers } from '../Icons/MapMarkers';
import { ItemsWithSectionId } from '../DiscoverPageSections';
import { GOOGLE_MAPS_MAP_ID } from '@/utils/googleMaps';

type Props = {
  guideItem: ItemsWithSectionId[];
  sectionsWithLocations: Section[];
  center: {
    lat: number;
    lng: number;
    zoom: number;
  };
  lang: Locale;
  title: string;
};

const defaultCenter = {
  lat: 37.7630382,
  lng: 13.486066,
};

export default function MapRoot({
  guideItem,
  lang,
  title,
  sectionsWithLocations,
  center,
}: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [zoom, setZoom] = useState(center.zoom || 8);
  const [showSections, setShowSections] = useState<number[]>(
    sectionsWithLocations.map((section) => section.id),
  );

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || '',
  });

  const {
    infoWindowData,
    showMarkerInfoWindow,
    infoWindowPositon,
    setInfoWindowData,
    setInfoWindowPosition,
    setIsOpen,
    isOpen,
    mapRef,
  } = useMap();

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map: google.maps.Map) {
    setMap(null);
  }, []);

  function showClusterInfoData(clusterer: Cluster) {
    const clusterPosition = clusterer.getCenter()?.toJSON();
    const clusterMarkersLocation = clusterer.getMarkers().map((marker) => {
      return {
        lat: marker.getPosition()?.lat(),
        lng: marker.getPosition()?.lng(),
      };
    });

    const clusterData = guideItem.filter((item) => {
      return clusterMarkersLocation.some(
        (markerPosition) =>
          markerPosition.lat === item?.surrounding?.location?.latitude,
      );
    });

    setInfoWindowPosition(clusterPosition);
    setInfoWindowData(clusterData);
    setIsOpen(true);
  }

  const changeShowSections = (id: number) => {
    setShowSections((prevState) => {
      return prevState.includes(id)
        ? prevState.filter((sectionId) => sectionId !== id)
        : [...prevState, id];
    });
  };

  useEffect(() => {}, [zoom]);

  return (
    <div className="relative w-full flex flex-col">
      <div
        ref={mapRef}
        className="w-full tablet:w-auto tablet:absolute p-5 top-3 z-10 left-3 bg-white">
        <h2 className="font-text w-[265px] text-subtitle font-semibold">
          - {title}
        </h2>
        <div className="flex flex-col items-start">
          {sectionsWithLocations.map((section) => (
            <button
              className="flex flex-row h-11 space-x-2.5 items-center"
              key={section.id}
              onClick={() => changeShowSections(section.id)}>
              <div
                className={`w-6 h-6 rounded-full border`}
                style={{
                  backgroundColor: showSections.includes(section.id)
                    ? section.iconColor || '#DF1CEA'
                    : '#F9F9F9',
                }}
              />
              <p>
                {
                  getTranslation(section?.guideSectionTranslation || [], lang)
                    .title
                }
              </p>
            </button>
          ))}
        </div>
      </div>

      {isLoaded && (
        <GoogleMap
          mapContainerClassName="relative overflow-hidden w-full h-[600px]"
          center={center || defaultCenter}
          zoom={zoom}
          onZoomChanged={() => setZoom(map?.getZoom() || center.zoom)}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            mapId: GOOGLE_MAPS_MAP_ID,
            fullscreenControl: false,
            zoomControlOptions: { position: 3.0 },
            tilt: 8,
            zoom,
            minZoom: 8,
            isFractionalZoomEnabled: false,
            keyboardShortcuts: false,
            streetViewControl: false,
            mapTypeControl: false,
          }}>
          <MarkerClusterer
            clusterClass="cluster"
            zoomOnClick={false}
            onClick={(cluster) => showClusterInfoData(cluster)}>
            {(clusterer) => (
              <>
                {isOpen && (
                  <InfoWindow
                    options={{
                      position: {
                        lat: infoWindowPositon?.lat || 0,
                        lng: infoWindowPositon?.lng || 0,
                      },
                      pixelOffset: new window.google.maps.Size(0, -60),
                    }}
                    onCloseClick={() => {
                      setIsOpen(false);
                    }}>
                    <MapCarousel lang={lang} GuideItems={infoWindowData} />
                  </InfoWindow>
                )}
                {guideItem
                  .filter((item) => showSections.includes(item.sectionId))
                  .map((guideItem) => {
                    const markerIcon =
                      sectionsWithLocations.filter(
                        (section) => section.id === guideItem.sectionId,
                      )[0].icon || 'locality';

                    return (
                      guideItem.surrounding && (
                        <Marker
                          icon={{
                            url: `https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/maps/markers/${MapMarkers(
                              {
                                iconName: markerIcon,
                              },
                            )}.png`,
                            scaledSize: new google.maps.Size(55, 55),
                          }}
                          key={guideItem.id}
                          position={{
                            lat:
                              guideItem?.surrounding?.location?.latitude || 0,
                            lng:
                              guideItem?.surrounding?.location?.longitude || 0,
                          }}
                          onClick={() => showMarkerInfoWindow(guideItem)}
                        />
                      )
                    );
                  })}
              </>
            )}
          </MarkerClusterer>
        </GoogleMap>
      )}
    </div>
  );
}
